import React, { useState } from 'react';
import {
  Box, Grid, Paper, Typography, ToggleButton, ToggleButtonGroup, Skeleton,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { TrendingUp, TrendingDown, TrendingFlat } from '@mui/icons-material';
import { LineChart, Line, ResponsiveContainer, Tooltip } from 'recharts';
import { seoAPI } from '../../services/api';

/**
 * Window-over-window summary: impressions / clicks / avg position / CTR for
 * the last N days vs the prior N days. Each metric shows current value, %
 * delta, and a sparkline. Lead with this on the SEO dashboard so the user
 * can answer "did things get better or worse?" in 5 seconds.
 */

const fmt = (n: number | null | undefined, digits = 0) =>
  n == null ? '—' : Number(n).toLocaleString(undefined, {
    minimumFractionDigits: digits, maximumFractionDigits: digits,
  });

const fmtPct = (n: number | null | undefined) =>
  n == null ? '—' : `${n >= 0 ? '+' : ''}${n.toFixed(1)}%`;

interface DeltaProps {
  delta: number | null | undefined;
  goodWhenPositive?: boolean;
}
const Delta: React.FC<DeltaProps> = ({ delta, goodWhenPositive = true }) => {
  if (delta == null) return <Typography variant="caption" sx={{ color: 'text.secondary' }}>—</Typography>;
  const isFlat = Math.abs(delta) < 1;
  const isGood = goodWhenPositive ? delta > 0 : delta < 0;
  const Icon = isFlat ? TrendingFlat : (delta > 0 ? TrendingUp : TrendingDown);
  const color = isFlat ? 'text.secondary' : (isGood ? 'success.main' : 'error.main');
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color }}>
      <Icon fontSize="small" />
      <Typography variant="body2" sx={{ color, fontWeight: 600 }}>
        {fmtPct(delta)}
      </Typography>
    </Box>
  );
};

interface MetricCardProps {
  label: string;
  current: number | null;
  delta: number | null;
  series: number[];
  formatValue?: (v: number) => string;
  goodWhenPositive?: boolean;
  windowLabel: string;
}

const MetricCard: React.FC<MetricCardProps> = ({
  label, current, delta, series, formatValue, goodWhenPositive = true, windowLabel,
}) => {
  const sparkData = series.map((v, i) => ({ i, v }));
  return (
    <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
      <Typography variant="overline" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1 }}>
          {formatValue ? (current != null ? formatValue(current) : '—') : fmt(current)}
        </Typography>
        <Delta delta={delta} goodWhenPositive={goodWhenPositive} />
      </Box>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
        vs prior {windowLabel}
      </Typography>
      <Box sx={{ height: 50, mt: 0.5 }}>
        {sparkData.length > 1 && (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={sparkData}>
              <Tooltip
                content={({ payload }: any) => {
                  if (!payload?.[0]) return null;
                  const v = payload[0].payload.v;
                  return (
                    <Box sx={{ background: '#0a0a0a', border: '1px solid #1e3a1e', px: 1, py: 0.5 }}>
                      <Typography variant="caption" sx={{ color: '#bfffbf' }}>
                        {formatValue ? formatValue(v) : fmt(v)}
                      </Typography>
                    </Box>
                  );
                }}
              />
              <Line
                type="monotone" dataKey="v"
                stroke={goodWhenPositive && (delta ?? 0) >= 0 ? '#5aaa5a' : (!goodWhenPositive && (delta ?? 0) <= 0 ? '#5aaa5a' : '#ffb000')}
                strokeWidth={2} dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </Box>
    </Paper>
  );
};

interface Props {
  site?: string;
}
const TrendsCard: React.FC<Props> = ({ site: externalSite }) => {
  const [days, setDays] = useState<number>(28);
  const [site, setSite] = useState<string>(externalSite || 'all');

  const effectiveSite = site === 'all' ? undefined : site;

  const { data, isLoading } = useQuery({
    queryKey: ['seo-trends', effectiveSite, days],
    queryFn: () => seoAPI.getTrendsSummary({ site: effectiveSite, days }),
    staleTime: 5 * 60_000,
  });

  if (isLoading) {
    return <Skeleton variant="rectangular" height={180} />;
  }
  if (!data) return null;

  const dailyImpr  = (data.daily || []).map((d: any) => d.impressions || 0);
  const dailyClk   = (data.daily || []).map((d: any) => d.clicks || 0);
  const dailyPos   = (data.daily || []).map((d: any) => d.avg_position || 0);
  const dailyCtr   = (data.daily || []).map((d: any) => d.impressions ? (d.clicks / d.impressions) : 0);

  const windowLabel = `${days}d`;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>Search trends</Typography>
        {!externalSite && (
          <ToggleButtonGroup size="small" exclusive value={site} onChange={(_, v) => v && setSite(v)}>
            <ToggleButton value="all">all</ToggleButton>
            <ToggleButton value="spoutlore">SL</ToggleButton>
            <ToggleButton value="mallbrats">MB</ToggleButton>
          </ToggleButtonGroup>
        )}
        <ToggleButtonGroup size="small" exclusive value={days} onChange={(_, v) => v && setDays(v)}>
          <ToggleButton value={7}>7d</ToggleButton>
          <ToggleButton value={28}>28d</ToggleButton>
          <ToggleButton value={90}>90d</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MetricCard
            label="impressions"
            current={data.impressions?.current}
            delta={data.impressions?.delta_pct}
            series={dailyImpr}
            windowLabel={windowLabel}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MetricCard
            label="clicks"
            current={data.clicks?.current}
            delta={data.clicks?.delta_pct}
            series={dailyClk}
            windowLabel={windowLabel}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MetricCard
            label="avg position"
            current={data.avg_position?.current}
            delta={data.avg_position?.delta != null
              ? (data.avg_position.previous ? (data.avg_position.delta / data.avg_position.previous * 100) : 0)
              : null}
            series={dailyPos}
            formatValue={(v) => v.toFixed(1)}
            goodWhenPositive={true /* lower is better, so we already inverted in backend (delta = prior - current). positive delta = position improved. */}
            windowLabel={windowLabel}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MetricCard
            label="CTR"
            current={data.ctr?.current != null ? data.ctr.current * 100 : null}
            delta={data.ctr?.delta_pct}
            series={dailyCtr.map((v: number) => v * 100)}
            formatValue={(v) => `${v.toFixed(2)}%`}
            windowLabel={windowLabel}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default TrendsCard;
